import dayjs from "dayjs";

export type DualAxisSeries = {
  yAxisId: "left" | "right";
  yKey: string;
  label: string;
  unit: string;
  color: string;
  dotShape: "circle" | "line" | "square" | "triangle";
  lineOpacity?: number;
  strokeDasharray?: string;
  lineJointType?: "monotoneX" | "linear";
};

export type DualAxisDataPoint = {
  time: string;
  unformattedTime: number;
  [key: string]: string | number | undefined;
};

export type ComparableMeasurement = {
  key: string;
  label: string;
  unit: string;
  color: string;
  dotShape: "circle" | "line" | "square" | "triangle";
};

// Seeded random for stable data across renders
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function generatePoints(
  key: string,
  count: number,
  intervalDays: number,
  baseFn: (i: number) => number,
  noiseFactor: number,
  seed: number,
  decimals: number = 2
): DualAxisDataPoint[] {
  const points: DualAxisDataPoint[] = [];
  const start = dayjs().subtract(10, "month");
  const rand = seededRandom(seed);

  for (let i = 0; i < count; i += 1) {
    const hour = 7 + Math.floor(rand() * 12);
    const minute = Math.floor(rand() * 60);
    const date = start
      .add(i * intervalDays, "day")
      .hour(hour)
      .minute(minute);
    const base = baseFn(i);
    const noise = (rand() - 0.5) * noiseFactor;
    const value = Math.max(0, base + noise);
    const factor = 10 ** decimals;
    points.push({
      time: date.format("HH:mm, MMM DD, YYYY"),
      unformattedTime: date.valueOf(),
      [key]: Math.round(value * factor) / factor,
    });
  }
  return points;
}

export const COMPARABLE_MEASUREMENTS: ComparableMeasurement[] = [
  {
    key: "cf_dna",
    label: "cfDNA",
    unit: "%",
    color: "#5B5FC7",
    dotShape: "circle",
  },
  {
    key: "fvc",
    label: "FVC",
    unit: "L",
    color: "#D4A843",
    dotShape: "square",
  },
  {
    key: "fev1_fvc",
    label: "FEV1/FVC",
    unit: "%",
    color: "#2E8B57",
    dotShape: "triangle",
  },
  {
    key: "fev6",
    label: "FEV6",
    unit: "L",
    color: "#8B4513",
    dotShape: "circle",
  },
  {
    key: "pef",
    label: "PEF",
    unit: "L/s",
    color: "#DC143C",
    dotShape: "square",
  },
  {
    key: "heart_rate",
    label: "Heart Rate",
    unit: "bpm",
    color: "#E91E63",
    dotShape: "circle",
  },
  {
    key: "pulse_ox",
    label: "SpO2",
    unit: "%",
    color: "#009688",
    dotShape: "triangle",
  },
  {
    key: "weight",
    label: "Weight",
    unit: "kg",
    color: "#607D8B",
    dotShape: "square",
  },
];

// Clinically realistic generators:
// - FEV1: adult male ~3-4L, slight decline post-transplant, daily variability ~0.15L
// - FVC: slightly higher than FEV1, ~3.5-4.5L range
// - FEV1/FVC: healthy >70%, transplant patients 75-85%
// - FEV6: close to FVC, ~3.4-4.3L
// - PEF: 5-10 L/s for adults, fluctuates with infections
// - Heart rate: resting 60-90 bpm, elevated during illness
// - SpO2: healthy 95-99%, transplant patients sometimes 92-98%
// - Weight: stable ~70-80kg with gradual drift
// - cfDNA: transplant marker, 0.2-1.2%, spikes indicate rejection
const MOCK_DATA_GENERATORS: Record<string, () => DualAxisDataPoint[]> = {
  cf_dna: () =>
    generatePoints(
      "cf_dna",
      4,
      90,
      (i) => {
        // Quarterly lab draws: baseline 0.3%, spike at Q2 (possible rejection), recovery Q3-Q4
        if (i === 1) return 0.95;
        if (i === 2) return 0.5;
        return 0.32 + i * 0.02;
      },
      0.04,
      42
    ),
  fvc: () =>
    generatePoints(
      "fvc",
      40,
      7,
      (i) => {
        // Post-transplant patient: starts at 3.9L, mild dip at week 12-16 (infection), recovery
        const baseline = 3.9;
        const infectionDip =
          i >= 12 && i <= 16 ? -0.3 * Math.sin(((i - 12) / 4) * Math.PI) : 0;
        const trend = i * 0.005; // slow improvement over time
        return baseline + trend + infectionDip;
      },
      0.12,
      101
    ),
  fev1_fvc: () =>
    generatePoints(
      "fev1_fvc",
      40,
      7,
      (i) => {
        // Ratio stays 76-82%, slight decrease during infection
        const baseline = 79;
        const infectionDip = i >= 12 && i <= 16 ? -2.5 : 0;
        return baseline + Math.sin(i * 0.2) * 1.5 + infectionDip;
      },
      1.2,
      202,
      1
    ),
  fev6: () =>
    generatePoints(
      "fev6",
      40,
      7,
      (i) => {
        // Close to FVC but slightly lower, ~3.6-4.1L
        const baseline = 3.75;
        const infectionDip = i >= 12 && i <= 16 ? -0.25 : 0;
        const trend = i * 0.004;
        return baseline + trend + infectionDip + Math.sin(i * 0.15) * 0.1;
      },
      0.1,
      303
    ),
  pef: () =>
    generatePoints(
      "pef",
      40,
      7,
      (i) => {
        // 6-9 L/s for adults, drops during respiratory infections
        const baseline = 7.5;
        const infectionDip = i >= 12 && i <= 16 ? -1.5 : 0;
        return baseline + Math.sin(i * 0.1) * 0.4 + infectionDip;
      },
      0.5,
      404,
      1
    ),
  heart_rate: () =>
    generatePoints(
      "heart_rate",
      50,
      6,
      (i) => {
        // Resting 68-78 bpm, elevated 85-95 during infection weeks
        const baseline = 72;
        const infectionBump = i >= 20 && i <= 28 ? 15 : 0;
        return baseline + Math.sin(i * 0.3) * 4 + infectionBump;
      },
      3,
      505,
      0
    ),
  pulse_ox: () =>
    generatePoints(
      "pulse_ox",
      50,
      6,
      (i) => {
        // 95-98% normally, dips to 92-94% during infection
        const baseline = 96.5;
        const infectionDip = i >= 20 && i <= 28 ? -2.5 : 0;
        return baseline + Math.sin(i * 0.15) * 0.8 + infectionDip;
      },
      0.5,
      606,
      0
    ),
  weight: () =>
    generatePoints(
      "weight",
      30,
      10,
      (i) => {
        // Stable 74-76 kg, slight loss during illness, gradual regain
        const baseline = 75.2;
        const illnessLoss = i >= 12 && i <= 18 ? -1.2 : 0;
        const trend = i > 18 ? (i - 18) * 0.08 : 0;
        return baseline + illnessLoss + trend;
      },
      0.3,
      707,
      1
    ),
};

// Cache generated data so it stays stable across renders
const dataCache: Record<string, DualAxisDataPoint[]> = {};

export function getMockData(key: string): DualAxisDataPoint[] {
  if (!dataCache[key]) {
    const generator = MOCK_DATA_GENERATORS[key];
    dataCache[key] = generator ? generator() : [];
  }
  return dataCache[key];
}

export const MOCK_SERIES: DualAxisSeries[] = [
  {
    yAxisId: "left",
    yKey: "fev1",
    label: "FEV1, MIR Spirobank Smart",
    unit: "L",
    color: "#C47E2B",
    dotShape: "circle",
    lineOpacity: 1,
  },
  {
    yAxisId: "right",
    yKey: "cf_dna",
    label: "cfDNA, Labs",
    unit: "%",
    color: "#5B5FC7",
    dotShape: "circle",
    lineOpacity: 0.6,
    strokeDasharray: "6 4",
    lineJointType: "linear",
  },
];

// FEV1: post-transplant patient, baseline ~3.1L with gradual improvement,
// dip around months 4-5 (infection/rejection episode), recovery afterwards
export const MOCK_FEV1_DATA = generatePoints(
  "fev1",
  60,
  5,
  (i) => {
    const baseline = 3.1;
    const improvement = i * 0.008;
    const infectionDip =
      i >= 24 && i <= 32 ? -0.35 * Math.sin(((i - 24) / 8) * Math.PI) : 0;
    return baseline + improvement + infectionDip;
  },
  0.08,
  1234
);
export const MOCK_CFDNA_DATA = getMockData("cf_dna");
